// 入库记录服务
import request from '../utils/request';
import type {
  InboundRecord,
  InboundRecordListResponse
} from '../types/inbound-record';

import { DataValidator } from '../utils/dataValidation';
import { ErrorHandler } from '../utils/errorHandler';

// 获取入库记录列表
export const getInboundRecordList = async (params: Record<string, any> = {}): Promise<InboundRecordListResponse> => {
  try {
    const response = await request.get('/api/v1/inbound/inbound-records/', { params });
    
    // 使用统一的数据验证工具
    const validatedResponse = DataValidator.validatePaginatedResponse<InboundRecord>(response.data);
    
    // 转换为期望的格式
    return {
      results: validatedResponse.results,
      count: validatedResponse.count,
      current: params.page || 1,
      pageSize: params.page_size || 10
    };
  } catch (error) {
    ErrorHandler.handleApiError(error, '获取入库记录列表');
    throw error;
  }
};

// 获取入库记录详情
export const getInboundRecordDetail = async (id: number): Promise<InboundRecord> => {
  const response = await request.get(`/api/v1/inbound/inbound-records/${id}/`);
  return response.data;
};

// 作废（软删除）入库记录
export const voidInboundRecord = async (id: number, payload: { reason?: string } = {}): Promise<void> => {
  await request.post(`/api/v1/inbound/inbound-records/${id}/void/`, payload);
};

// 修改入库记录（差额补正）
export const modifyInboundRecord = async (
  id: number,
  payload: { items: Array<{ inventory_unit: number; new_quantity: number; remarks?: string }>; reason?: string }
): Promise<void> => {
  await request.patch(`/api/v1/inbound/inbound-records/${id}/modify/`, payload);
};

// 修改（或解绑）入库记录关联的入库预报：inbound_forecast传null/不传表示解绑
export const updateInboundRecordForecastLink = async (
  id: number,
  payload: { inbound_forecast: number | null }
): Promise<void> => {
  await request.patch(`/api/v1/inbound/inbound-records/${id}/update-forecast-link/`, payload);
};

export const downloadInboundRecords = async (params: any): Promise<void> => {
  const response = await request.post('/api/v1/inbound/inbound-records-download/', params, {
    responseType: 'blob',
  });
  
  // 创建下载链接
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  // 生成当前日期时间
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  
  // 设置文件名：入库记录_当前日期时间.xlsx
  const contentDisposition = response.headers['content-disposition'];
  let filename = `入库记录_${year}-${month}-${day}_${hours}-${minutes}-${seconds}.xlsx`;
  
  // 如果后端返回了文件名，则使用后端的文件名
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// 获取入库记录列表（分页）
export const getInboundRecordListPaginated = async (
  page: number = 1,
  pageSize: number = 10,
  filters: Record<string, any> = {}
): Promise<InboundRecordListResponse> => {
  const params = {
    page,
    page_size: pageSize,
    ...filters
  };
  
  return getInboundRecordList(params);
};

// 查询入库余额（用于自动拆分）
export const getInboundUnitBalances = async (payload: { project_code: string; warehouse_code: string; unit_codes: string[] }) => {
  const resp = await request.post('/api/v1/inbound/records/balances/', payload);
  return resp.data;
};